/** `cancelled` only appears when cancelled items are explicitly requested (e.g. the CANCELLED state filter) */
export type WorkQueueItemStatus = 'not_started' | 'in_progress' | 'completed' | 'cancelled';

/** One record in the signed-in user's work queue (e.g. an inspection request). */
export interface WorkQueueItem {
  id: string;
  /** Number shown to the user, e.g. the inspection request id */
  request_number: string;
  /** ISO 8601 timestamp */
  created_at: string;
  customer: string;
  city: string;
  state: string;
  zip: string;
  status: WorkQueueItemStatus;
  // Set when the item comes from a SOR queue_item record (see mapQueueItemRecord)
  /** Full queue item title, e.g. "Inspection CE-PILOT-001" */
  title?: string;
  queue_id?: string;
  /** Record being worked (e.g. the inspection) */
  source_record_id?: string;
  source_definition_id?: string;
  /** Display name of the source definition, e.g. "Inspection"; used in the item's action labels */
  source_definition_display?: string;
  priority?: number;
  /** ISO 8601 timestamp */
  due_at?: string | null;
}

/** An entity type the user can create from the queue, e.g. Inspection (an enabled queue_entities record) */
export interface WorkQueueCreateOption {
  /** The entity's SOR definition id */
  definitionId: string;
  /** SOR definition key, e.g. "slm.insp.inspection" */
  key: string;
  /** Shown as "Create {label}", e.g. "Inspection" */
  label: string;
}

export type WorkQueueSortOrder = 'newest' | 'oldest';

export interface WorkQueueFilters {
  requestNumber: string;
  customer: string;
  state: string;
  status: WorkQueueItemStatus | '';
  sort: WorkQueueSortOrder;
}

export const EMPTY_WORK_QUEUE_FILTERS: WorkQueueFilters = {
  requestNumber: '',
  customer: '',
  state: '',
  status: '',
  sort: 'newest',
};

export interface WorkQueueStatusConfig {
  label: string;
  icon: string;
  /** Text / accent colour */
  textClass: string;
  /** Soft background for the icon bubble and pill */
  bgClass: string;
  /** Left accent border on list items */
  borderClass: string;
}

export const WORK_QUEUE_STATUS_CONFIG: Record<WorkQueueItemStatus, WorkQueueStatusConfig> = {
  not_started: {
    label: 'Not Started',
    icon: 'mdi:circle-outline',
    textClass: 'text-default-500',
    bgClass: 'bg-default-100',
    borderClass: 'border-l-default-400',
  },
  in_progress: {
    label: 'In Progress',
    icon: 'mdi:clock-outline',
    textClass: 'text-warning-600',
    bgClass: 'bg-warning-50',
    borderClass: 'border-l-warning-600',
  },
  completed: {
    label: 'Completed',
    icon: 'mdi:check',
    textClass: 'text-blue-600',
    bgClass: 'bg-blue-50 dark:bg-blue-500/15',
    borderClass: 'border-l-blue-600',
  },
  cancelled: {
    label: 'Cancelled',
    icon: 'mdi:close',
    textClass: 'text-danger-600',
    bgClass: 'bg-danger-50',
    borderClass: 'border-l-danger-600',
  },
};

/** Statuses with a counter card; cancelled items are rare in the list and have no card */
export const WORK_QUEUE_STATUSES: WorkQueueItemStatus[] = ['not_started', 'in_progress', 'completed'];

/** Option for a filter dropdown */
export interface WorkQueueFilterOption {
  key: string;
  label: string;
}

export const DEFAULT_WORK_QUEUE_CANCEL_REASONS = [
  'Claim withdrawn by customer',
  'Duplicate request',
  'Repair already completed',
  'Vehicle not available',
  'Other',
];
